import { z } from "zod";

import { VISIT_TYPES } from "./constants";
import { VisitType } from "./types";

export const appointmentTypeSchema = z.object({
  id: z.number(),
  name: z.string(),
  duration: z.number(),
  buffer: z.number().nullable(),
  price: z.number().nullable(),
  slug: z.string().nullable(),
});

export const appointmentSlotSchema = z.object({
  id: z.number(),
  startTime: z.string(),
  duration: z.number(),
  originalTime: z.string().optional(),
  providerId: z.number().optional(),
  providerName: z.string().optional(),
  providerTitle: z.string().optional(),
});

const baseProviderSchema = z.object({
  id: z.number(),
  name: z.string(),
  title: z.union([z.string(), z.array(z.string())]),
  description: z.string().optional(),
  image: z.string().nullable().optional(),
});

export const normalizeTitle = <T extends { title: string | string[] }>(
  data: T,
): T & { title: string } => ({
  ...data,
  title: Array.isArray(data.title) ? data.title.join(", ") : data.title,
});

export const appointmentProviderSchema = baseProviderSchema
  .pick({ name: true, title: true })
  .transform(normalizeTitle);

export const dateSlotSchema = z.record(
  z.string(),
  z.array(appointmentSlotSchema),
);

export const providerSchema = baseProviderSchema
  .extend({
    timezone: z.string().optional(),
    slots: dateSlotSchema,
    first_name: z.string().optional(),
    last_name: z.string().optional(),
    languages: z.array(z.string()).optional().default([]),
    care_philosophy: z.string().nullable().optional(),
    additional_expertise: z.array(z.string()).optional().default([]),
    years_of_experience: z.number().nullable().optional(),
    testimonials: z.array(z.string()).optional().default([]),
  })
  .transform(normalizeTitle)
  .transform(
    ({
      first_name,
      last_name,
      care_philosophy,
      additional_expertise,
      years_of_experience,
      ...rest
    }) => ({
      ...rest,
      firstName: first_name,
      lastName: last_name,
      carePhilosophy: care_philosophy,
      additionalExpertise: additional_expertise,
      yearsOfExperience: years_of_experience,
    }),
  );

export const AppointmentType = z.enum(
  Object.values(VISIT_TYPES) as [VisitType, ...VisitType[]],
);

export const getAppointmentSlotsParamsSchema = z.object({
  stateId: z.number().nullable().optional(),
  appointmentType: z.string(),
  startDate: z.string(),
  dateRange: z.string(),
  providerId: z.number().optional(),
  insuranceId: z.number().nullable().optional(),
});

export const providerWithoutSlotsSchema =
  baseProviderSchema.transform(normalizeTitle);

export const appointmentSlotsQueryParamsSchema = z.object({
  stateId: z.number(),
  appointmentType: z.string(),
  startDate: z.string(),
  dateRange: z.string(),
});

export const patientVisitSchema = z.object({
  stateId: z.number().nullable().optional(),
  visitDateTime: z.string(),
  appointmentType: z.string(),
  providerId: z.number(),
  patientStateId: z.number().nullable().optional(),

  patientFirstName: z
    .string()
    .max(20, "First name must be less than 20 characters"),
  patientLastName: z
    .string()
    .max(20, "Last name must be less than 20 characters"),
  patientDob: z.string(),
  patientSexAtBirth: z.string(),
  patientMobilePhoneNumber: z.string(),
  patientEmail: z.string().optional(),

  patientAddressOne: z.string(),
  patientAddressTwo: z.string().optional().nullable(),
  patientCity: z.string(),
  patientZipCode: z.string(),

  patientHowDidYouHear: z.string().optional(),

  insuranceId: z.number().optional().nullable(),
  insuranceCompanyName: z.string().optional(),
  insuranceRelationshipToPolicyHolder: z.string().optional(),
  insurancePolicyHolderFirstName: z.string().optional(),
  insurancePolicyHolderLastName: z.string().optional(),
  insurancePolicyHolderDob: z.string().optional(),
  insuranceCardFront: z
    .union([z.instanceof(File), z.string(), z.null()])
    .optional(),
  insuranceCardBack: z
    .union([z.instanceof(File), z.string(), z.null()])
    .optional(),
  insurancePolicyNumber: z.string().optional(),
  nirvana_insurance_key: z.string().optional(),
});

export const appointmentSchema = z.object({
  id: z.string(),
  type: z.string(),
  date: z.string(),
  startTime: z.string(),
  duration: z.number(),
  inviteLink: z.string().url().optional(),
});

export const providerResponseSchema = z.object({
  id: z.number(),
  title: z.string(),
  name: z.string(),
  imageUrl: z.string().nullable(),
});

export const scheduleVisitResponseSchema = z.object({
  provider: providerResponseSchema,
  appointment: appointmentSchema,
  user: z
    .object({
      hash: z.string(),
      emailVerified: z.boolean(),
    })
    .optional(),
});

export const adminScheduleVisitResponseSchema =
  scheduleVisitResponseSchema.extend({
    user: z.object({
      id: z.string(),
      hash: z.string(),
      emailVerified: z.boolean(),
    }),
  });

/** Snake_case on purpose: it is the same object the edit cancellation endpoint returns, kept to one shape for both. */
export const visitCancellationSchema = z.object({
  appointment_cancel_reason_id: z.number().nullable(),
  parent_cancel_reason_id: z.number().nullable(),
  cancel_reason_name: z.string().nullable(),
  cancellation_note: z.string().nullable(),
  late_cancellation_fee_waived: z.boolean().nullable(),
  cancelled_at: z.string().nullable(),
  reason_editable: z.boolean(),
});

export const scheduledAppointmentSchema = z.object({
  id: z.string(),
  date: z.string(),
  type: z.string(),
  provider: appointmentProviderSchema,
  department: z.string(),
  status: z.string(),
  cancelReason: z.string().nullable().optional(),
  cancellation: visitCancellationSchema.optional(),
});

export const patientInsuranceSchema = z.object({
  ehrId: z.string(),
  name: z.string(),
});

export const userAppointmentsResponseSchema = z.object({
  upcomingAppointments: z.array(scheduledAppointmentSchema),
  appointmentsHistory: z.array(scheduledAppointmentSchema),
  insurance: patientInsuranceSchema.optional(),
});

export const staffSchedulingSchema = patientVisitSchema.extend({
  patientFirstName: patientVisitSchema.shape.patientFirstName.optional(),
  patientLastName: patientVisitSchema.shape.patientLastName.optional(),
  patientDob: patientVisitSchema.shape.patientDob.optional(),
  patientSexAtBirth: patientVisitSchema.shape.patientSexAtBirth.optional(),
  patientMobilePhoneNumber:
    patientVisitSchema.shape.patientMobilePhoneNumber.optional(),
  patientAddressOne: patientVisitSchema.shape.patientAddressOne.optional(),
  patientCity: patientVisitSchema.shape.patientCity.optional(),
  patientZipCode: patientVisitSchema.shape.patientZipCode.optional(),
  userId: z.string().optional(),
  insuranceGroupNumber: z.string().optional(),
  bookingNotes: z.string().optional(),
  bookingFlowId: z.string().optional(),
  addressChanged: z.boolean().optional(),
  insuranceChanged: z.boolean().optional(),
});
